import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Skeleton, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, Chip, LinearProgress, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, Divider, useMediaQuery, useTheme,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as SessionIcon,
  TrendingUp as TrendingIcon,
  Timer as TimerIcon,
  LocationOn as LocationIcon,
  TabletMac as TabletIcon,
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  MonitorHeart as HeartIcon,
  Warning,
  FileDownload as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { dashboardAPI, analyticsAPI, locationAPI, patientAPI, misAPI } from '../services/api';

const BLUE = '#00A8E8';
const GREEN = '#86E000';
const DARK_BLUE = '#005B8F';

const statCardConfigs = [
  { key: 'todayPatients', label: "Today's Patients", icon: PeopleIcon, gradient: 'linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)', shadow: 'rgba(8,145,178,0.3)', lightBg: '#ecfeff', navigateTo: '/patients?startDate=TODAY' },
  { key: 'todaySessions', label: "Tests Done Today", icon: CheckIcon, gradient: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)', shadow: 'rgba(245,158,11,0.3)', lightBg: '#fffbeb', navigateTo: '/patients?sessionDate=TODAY' },
  { key: 'totalPatients', label: 'Total Patients', icon: TrendingIcon, gradient: 'linear-gradient(135deg, #059669 0%, #10b981 100%)', shadow: 'rgba(16,185,129,0.3)', lightBg: '#ecfdf5', navigateTo: '/patients' },
  { key: 'completedSessions', label: 'Total Completed', icon: SessionIcon, gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', shadow: 'rgba(99,102,241,0.3)', lightBg: '#eef2ff', navigateTo: '/patients?status=completed' },
  { key: 'avgTimeMinutes', label: 'Avg Time (min)', icon: TimerIcon, gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)', shadow: 'rgba(14,165,233,0.3)', lightBg: '#f0f9ff', navigateTo: null },
];

function KpiCard({ title, value, subtitle, icon, color = BLUE, trend }) {
  return (
    <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: '14px',
            background: `linear-gradient(135deg, ${color}22, ${color}44)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            {React.cloneElement(icon, { sx: { color, fontSize: 26 } })}
          </Box>
          {trend !== undefined && (
            <Chip
              label={`${trend >= 0 ? '+' : ''}${trend}%`}
              size="small"
              sx={{
                fontWeight: 700, fontSize: '0.7rem',
                bgcolor: trend >= 0 ? '#dcfce7' : '#fee2e2',
                color: trend >= 0 ? '#16a34a' : '#dc2626',
              }}
            />
          )}
        </Box>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 800, color: DARK_BLUE, lineHeight: 1, mb: 0.5 }}>
            {value ?? '—'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mb: 0.25 }}>{title}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color: '#94a3b8' }}>{subtitle}</Typography>}
        </Box>
      </CardContent>
    </Card>
  );
}

function BMIBar({ label, count, total, color }) {
  const pct = total ? Math.round((count / total) * 100) : 0;
  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>{label}</Typography>
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
          {count} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({pct}%)</span>
        </Typography>
      </Box>
      <Box sx={{ height: 10, bgcolor: '#f1f5f9', borderRadius: 5, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${pct}%`, bgcolor: color, borderRadius: 5, transition: 'width 1s ease' }} />
      </Box>
    </Box>
  );
}

export default function Analytics() {
  // Dashboard state
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState([]);
  const [dashLoading, setDashLoading] = useState(true);

  // Analytics state
  const [overview, setOverview] = useState(null);
  const [distribution, setDistribution] = useState(null);
  const [byLocation, setByLocation] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [error, setError] = useState('');

  // Shared filters
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');
  const [days, setDays] = useState(30);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [exportOpen, setExportOpen] = useState(false);
  const [exportStart, setExportStart] = useState('');
  const [exportEnd, setExportEnd] = useState('');
  const [exportLocation, setExportLocation] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState('');

  // Quick-export state (uses currently selected header filters)
  const [quickExportLoading, setQuickExportLoading] = useState(false);
  const [quickExportError, setQuickExportError] = useState('');

  // Human-friendly summary of what the quick-export will download
  const selectedLocationName = selectedLocation
    ? (locations.find(l => l._id === selectedLocation)?.name || 'Selected location')
    : 'All Locations';
  const periodLabel = `last ${days} day${days === 1 ? '' : 's'}`;
  // Best-effort patient count for the current filter — falls back gracefully
  const quickExportCount =
    overview?.totalPatients ?? stats?.totalPatients ?? null;
  const quickExportSummary = quickExportCount != null
    ? `${quickExportCount} patient${quickExportCount === 1 ? '' : 's'} (${selectedLocationName}, ${periodLabel})`
    : `${selectedLocationName}, ${periodLabel}`;

  // MIS Report state — default date range: last 30 days
  const misDefaultEnd = new Date().toISOString().slice(0, 10);
  const misDefaultStart = (() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().slice(0, 10);
  })();
  const [misStart, setMisStart] = useState(misDefaultStart);
  const [misEnd, setMisEnd] = useState(misDefaultEnd);
  const [misLocation, setMisLocation] = useState('');
  const [misData, setMisData] = useState(null);
  const [misLoading, setMisLoading] = useState(false);
  const [misError, setMisError] = useState('');

  const handleExport = async () => {
    if (!exportStart || !exportEnd) { setExportError('Please select both start and end dates.'); return; }
    if (new Date(exportStart) > new Date(exportEnd)) { setExportError('Start date must be before end date.'); return; }
    setExportError('');
    setExportLoading(true);
    try {
      const params = { startDate: exportStart, endDate: exportEnd };
      if (exportLocation) params.locationId = exportLocation;
      const res = await patientAPI.exportExcel(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `patients_${exportStart}_to_${exportEnd}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch {
      setExportError('Export failed. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  // Quick export using the header filters (Location + Period). Skips the dialog.
  const handleQuickExport = async () => {
    setQuickExportError('');
    setQuickExportLoading(true);
    try {
      // Convert the Period (days) into an explicit date range so the backend
      // filter matches exactly what the user sees on the Analytics page.
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - Number(days));
      const startDate = start.toISOString().slice(0, 10);
      const endDate   = end.toISOString().slice(0, 10);

      const params = { startDate, endDate };
      if (selectedLocation) params.locationId = selectedLocation;

      const res = await patientAPI.exportExcel(params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const locTag = selectedLocation
        ? (locations.find(l => l._id === selectedLocation)?.name || 'location').replace(/\s+/g, '_')
        : 'all';
      link.setAttribute('download', `patients_${locTag}_${startDate}_to_${endDate}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setQuickExportError('Export failed. Please try again.');
    } finally {
      setQuickExportLoading(false);
    }
  };

  const handleGenerateMis = async () => {
    if (!misStart || !misEnd) { setMisError('Please select both start and end dates.'); return; }
    if (new Date(misStart) > new Date(misEnd)) { setMisError('Start date must be before end date.'); return; }
    setMisError('');
    setMisLoading(true);
    setMisData(null);
    try {
      const params = { startDate: misStart, endDate: misEnd };
      if (misLocation) params.locationId = misLocation;
      const res = await misAPI.getMisReport(params);
      setMisData(res.data.data);
    } catch (err) {
      setMisError('Failed to generate MIS report. ' + (err.response?.data?.message || err.message));
    } finally {
      setMisLoading(false);
    }
  };

  const getMisTableRows = () => {
    if (!misData) return [];
    const { dailyBreakdown = [], locationBreakdown = [] } = misData;
    if (dailyBreakdown.length > 0) {
      return dailyBreakdown.map(row => {
        const locName = misLocation
          ? (locations.find(l => l._id === misLocation)?.name || misLocation)
          : 'All Locations';
        const rate = row.patients > 0 ? Math.round((row.completed / row.patients) * 100) : 0;
        return { date: row.date, location: locName, patients: row.patients, completed: row.completed, rate };
      });
    }
    return locationBreakdown.map(row => ({
      date: `${misStart} – ${misEnd}`,
      location: row.name || row.locationId,
      patients: row.patients,
      completed: row.completed,
      rate: row.patients > 0 ? Math.round((row.completed / row.patients) * 100) : 0,
    }));
  };

  const handleMisExportExcel = () => {
    const rows = getMisTableRows();
    if (!rows.length) return;
    const sheetData = rows.map(r => ({
      Date: r.date,
      Location: r.location,
      Patients: r.patients,
      'Completed Sessions': r.completed,
      'Completion Rate (%)': r.rate,
    }));
    const ws = XLSX.utils.json_to_sheet(sheetData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'MIS Report');
    XLSX.writeFile(wb, `MIS_Report_${misStart}_to_${misEnd}.xlsx`);
  };

  const handleMisExportPdf = () => {
    const rows = getMisTableRows();
    if (!rows.length) return;
    const tableRows = rows.map(r =>
      `<tr>
        <td>${r.date}</td>
        <td>${r.location}</td>
        <td style="text-align:center">${r.patients}</td>
        <td style="text-align:center">${r.completed}</td>
        <td style="text-align:center">${r.rate}%</td>
      </tr>`
    ).join('');
    const html = `<!DOCTYPE html>
<html>
<head>
  <title>MIS Report ${misStart} to ${misEnd}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 24px; color: #0f172a; }
    h2 { color: #005B8F; margin-bottom: 4px; }
    p { color: #64748b; font-size: 13px; margin-bottom: 16px; }
    table { border-collapse: collapse; width: 100%; font-size: 13px; }
    th { background: #0891b2; color: #fff; padding: 10px 12px; text-align: left; }
    td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; }
    tr:nth-child(even) td { background: #f8fafc; }
  </style>
</head>
<body>
  <h2>MIS Report</h2>
  <p>Period: ${misStart} to ${misEnd} | Generated: ${new Date().toLocaleString()}</p>
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Location</th><th>Patients</th><th>Completed</th><th>Completion Rate</th>
      </tr>
    </thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(html);
      win.document.close();
      win.focus();
      win.print();
    }
  };

  // Load locations once
  useEffect(() => {
    locationAPI.getAll().then(res => setLocations(res.data?.data || [])).catch(() => {});
  }, []);

  // Load dashboard stats
  useEffect(() => {
    setDashLoading(true);
    dashboardAPI.getStats(selectedLocation || undefined)
      .then(res => setStats(res.data.data))
      .catch(() => {})
      .finally(() => setDashLoading(false));

    if (selectedLocation) {
      dashboardAPI.getQueue(selectedLocation)
        .then(res => setQueue(res.data.data || []))
        .catch(() => {});
    } else {
      setQueue([]);
    }
  }, [selectedLocation]);

  // Load analytics data
  useEffect(() => {
    setAnalyticsLoading(true);
    setError('');
    const params = { days, ...(selectedLocation && { locationId: selectedLocation }) };
    Promise.all([
      analyticsAPI.getOverview(params),
      analyticsAPI.getDistribution(params),
      analyticsAPI.getByLocation(),
      analyticsAPI.getTrends({ ...params, metric: 'bmi' }),
    ]).then(([ov, dist, loc]) => {
      setOverview(ov.data?.data);
      setDistribution(dist.data?.data);
      setByLocation(loc.data?.data || []);
    }).catch(err => {
      setError('Failed to load analytics. ' + (err.response?.data?.message || err.message));
    }).finally(() => setAnalyticsLoading(false));
  }, [selectedLocation, days]);

  const bmiTotal = distribution?.bmiDistribution?.reduce((s, b) => s + b.count, 0) || 0;
  const bmiColors = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed'];
  const bmiLabels = ['Underweight (<18.5)', 'Normal (18.5–25)', 'Overweight (25–30)', 'Obese (30–35)', 'Severely Obese (35+)'];

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        mb: 3, flexWrap: 'wrap', gap: 2,
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <HeartIcon sx={{ color: '#0891b2', fontSize: 28 }} />
            <Typography variant={isSmall ? 'h5' : 'h4'} sx={{ fontWeight: 800, color: '#0f172a' }}>
              Analytics
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', ml: isSmall ? 0 : 4.5 }}>
            Overview and insights for your health kiosk operations
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 180 } }}>
            <InputLabel>Filter by Location</InputLabel>
            <Select
              value={selectedLocation} label="Filter by Location"
              onChange={e => setSelectedLocation(e.target.value)}
            >
              <MenuItem value="">All Locations</MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Period</InputLabel>
            <Select value={days} label="Period" onChange={e => setDays(e.target.value)}>
              <MenuItem value={7}>7 days</MenuItem>
              <MenuItem value={30}>30 days</MenuItem>
              <MenuItem value={90}>90 days</MenuItem>
              <MenuItem value={365}>1 year</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
            <Button
              variant="outlined"
              startIcon={quickExportLoading ? <CircularProgress size={16} sx={{ color: '#0891b2' }} /> : <DownloadIcon />}
              onClick={handleQuickExport}
              disabled={quickExportLoading}
              title={`Export Excel for ${quickExportSummary}`}
              sx={{
                borderColor: '#0891b2', color: '#0891b2', fontWeight: 600,
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#005B8F', color: '#005B8F', bgcolor: '#ecfeff' },
              }}
            >
              {quickExportLoading
                ? `Exporting ${quickExportSummary}…`
                : 'Export Excel'}
            </Button>
            <Button
              size="small"
              onClick={() => { setExportOpen(true); setExportError(''); }}
              sx={{
                textTransform: 'none', color: '#64748b', fontSize: '0.72rem',
                minWidth: 0, py: 0, px: 0.5,
                '&:hover': { bgcolor: 'transparent', color: '#0891b2', textDecoration: 'underline' },
              }}
            >
              Custom range…
            </Button>
          </Box>
        </Box>
      </Box>
      {quickExportError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setQuickExportError('')}>
          {quickExportError}
        </Alert>
      )}

      {/* ── DASHBOARD SECTION ─────────────────────────────────── */}

      {/* Primary Stat Cards */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
        {statCardConfigs.map((config, index) => (
          <Grid item xs={6} sm={6} lg={3} key={config.key}>
            <Card
              onClick={() => {
                if (!config.navigateTo) return;
                const todayStr = new Date().toISOString().slice(0, 10);
                navigate(config.navigateTo.replace('TODAY', todayStr));
              }}
              sx={{
                position: 'relative', overflow: 'hidden',
                transition: 'all 0.3s ease',
                cursor: config.navigateTo ? 'pointer' : 'default',
                '&:hover': {
                  transform: config.navigateTo ? 'translateY(-4px)' : 'none',
                  boxShadow: config.navigateTo ? `0 12px 30px ${config.shadow}` : undefined,
                },
              }}>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{
                      color: '#64748b', fontWeight: 500, mb: 1,
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                    }}>
                      {config.label}
                    </Typography>
                    {dashLoading ? (
                      <Skeleton width={60} height={40} />
                    ) : (
                      <Typography sx={{
                        fontWeight: 800, color: '#0f172a',
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.2rem' },
                        lineHeight: 1,
                      }}>
                        {stats?.[config.key] ?? 0}
                      </Typography>
                    )}
                  </Box>
                  <Avatar sx={{
                    width: { xs: 40, sm: 48 }, height: { xs: 40, sm: 48 },
                    background: config.gradient,
                    boxShadow: `0 4px 14px ${config.shadow}`,
                    flexShrink: 0,
                  }}>
                    <config.icon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                  </Avatar>
                </Box>
              </CardContent>
              <Box sx={{ height: 3, background: config.gradient }} />
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Secondary Stats */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }}>
        {[
          { label: 'Active Sessions', value: stats?.inProgressSessions ?? 0, icon: <SessionIcon />, bg: '#f0fdfa', color: '#0d9488', navigateTo: '/patients?status=in_progress' },
          { label: 'Pending Sessions', value: stats?.pendingSessions ?? 0, icon: <PendingIcon />, bg: '#fffbeb', color: '#d97706', navigateTo: '/patients?status=registered' },
          { label: 'Active Locations', value: stats?.totalLocations ?? 0, icon: <LocationIcon />, bg: '#eff6ff', color: '#2563eb', navigateTo: '/locations' },
          { label: 'Tablets Online', value: `${stats?.onlineTablets ?? 0}/${stats?.totalTablets ?? 0}`, icon: <TabletIcon />, bg: '#ecfdf5', color: '#059669', navigateTo: '/tablets' },
        ].map((item) => (
          <Grid item xs={6} md={3} key={item.label}>
            <Card
              onClick={() => navigate(item.navigateTo)}
              sx={{
                p: { xs: 1.5, sm: 2.5 },
                display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': { boxShadow: `0 6px 20px ${item.color}33`, transform: 'translateY(-2px)' },
              }}>
              <Avatar sx={{ bgcolor: item.bg, color: item.color, width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 } }}>
                {item.icon}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{
                  fontSize: { xs: '0.65rem', sm: '0.75rem' },
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {item.label}
                </Typography>
                <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                  {dashLoading ? <Skeleton width={30} /> : item.value}
                </Typography>
              </Box>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Location Overview + Patient Queue */}
      <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={7}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
                Location Overview
              </Typography>
              {locations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                  <LocationIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                  <Typography>No locations configured yet</Typography>
                  <Typography variant="body2">Go to Locations page to add one</Typography>
                </Box>
              ) : (
                <Box sx={{ overflowX: 'auto', mx: { xs: -2, sm: 0 } }}>
                  <TableContainer sx={{ minWidth: 500 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Location</TableCell>
                          <TableCell align="center">Stations</TableCell>
                          <TableCell align="center">Tablets</TableCell>
                          <TableCell align="center">Online</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {locations.map((loc) => (
                          <TableRow key={loc._id} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#ecfeff', color: '#0891b2', fontSize: '0.8rem', fontWeight: 600 }}>
                                  {loc.name[0]}
                                </Avatar>
                                <Typography variant="body2" fontWeight={500}>{loc.name}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label={loc.stationCount || loc.numStations} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell align="center">{loc.tabletCount ?? 0}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={loc.onlineTablets ?? 0}
                                size="small"
                                color={(loc.onlineTablets ?? 0) > 0 ? 'success' : 'default'}
                                variant={(loc.onlineTablets ?? 0) > 0 ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={loc.isActive ? 'Active' : 'Inactive'}
                                size="small"
                                color={loc.isActive ? 'success' : 'default'}
                                variant="filled"
                                sx={{ fontWeight: 500 }}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0f172a' }}>
                Patient Queue
              </Typography>
              {!selectedLocation ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                  <PeopleIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
                  <Typography>Select a location to view queue</Typography>
                </Box>
              ) : queue.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                  <CheckIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5, color: '#10b981' }} />
                  <Typography>No patients in queue</Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {queue.slice(0, 8).map((item) => {
                    const completed = item.stationResults?.filter(r => r.status === 'completed').length || 0;
                    const total = item.totalStations || 1;
                    const progress = Math.round((completed / total) * 100);
                    return (
                      <Box key={item._id} sx={{
                        p: 2, borderRadius: 3, border: '1px solid #e0f2fe',
                        transition: 'all 0.2s ease',
                        '&:hover': { borderColor: '#67e8f9', bgcolor: '#f0fdfa' },
                      }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', background: 'linear-gradient(135deg, #0891b2, #06b6d4)' }}>
                              {(item.patientId?.name || '?')[0]}
                            </Avatar>
                            <Typography variant="body2" fontWeight={600}>
                              {item.patientId?.name || 'Unknown'}
                            </Typography>
                          </Box>
                          <Chip
                            label={item.status === 'in_progress' ? 'In Progress' : 'Waiting'}
                            size="small"
                            color={item.status === 'in_progress' ? 'primary' : 'default'}
                            variant={item.status === 'in_progress' ? 'filled' : 'outlined'}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate" value={progress}
                            sx={{
                              flex: 1, height: 6, borderRadius: 3, bgcolor: '#e0f2fe',
                              '& .MuiLinearProgress-bar': { borderRadius: 3, background: 'linear-gradient(90deg, #0891b2, #06b6d4)' },
                            }}
                          />
                          <Typography variant="caption" sx={{ color: '#64748b', minWidth: 55 }}>
                            {completed}/{total} done
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── ANALYTICS SECTION ─────────────────────────────────── */}

      <Divider sx={{ mb: 3 }}>
        <Chip label="Health Insights" sx={{ fontWeight: 600, color: DARK_BLUE, bgcolor: '#f0f9ff', border: `1px solid ${BLUE}33` }} />
      </Divider>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {analyticsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: BLUE }} />
        </Box>
      ) : (
        <>
          {/* KPI Cards */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {[
              { title: 'Total Patients', value: overview?.totalPatients?.toLocaleString(), subtitle: 'Registered', icon: <PeopleIcon />, color: BLUE },
              { title: 'Vitals Recorded', value: overview?.totalVitalsRecorded?.toLocaleString(), subtitle: `Last ${days} days`, icon: <SessionIcon />, color: GREEN },
              { title: 'Sessions', value: overview?.totalSessions?.toLocaleString(), subtitle: `${overview?.completedSessions ?? 0} completed`, icon: <CheckIcon />, color: '#8b5cf6' },
              { title: 'Completion Rate', value: `${overview?.completionRate || 0}%`, subtitle: 'Sessions completed', icon: <TrendingIcon />, color: '#f59e0b' },
            ].map((card) => (
              <Grid item xs={12} sm={6} md={3} key={card.title} sx={{ display: 'flex' }}>
                <KpiCard {...card} />
              </Grid>
            ))}
          </Grid>

          {/* Avg Vitals */}
          {overview?.averages && (
            <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 2.5 }}>
                  Average Vitals — Population Overview
                </Typography>
                <Grid container spacing={2}>
                  {[
                    { label: 'Avg BMI', val: overview.averages.avgBMI?.toFixed(1), unit: 'kg/m²' },
                    { label: 'Avg Systolic BP', val: overview.averages.avgSystolic?.toFixed(0), unit: 'mmHg' },
                    { label: 'Avg Diastolic BP', val: overview.averages.avgDiastolic?.toFixed(0), unit: 'mmHg' },
                    { label: 'Avg Heart Rate', val: overview.averages.avgHeartRate?.toFixed(0), unit: 'bpm' },
                    { label: 'Avg Hemoglobin', val: overview.averages.avgHemoglobin?.toFixed(1), unit: 'g/dL' },
                    { label: 'Avg SpO₂', val: overview.averages.avgSpO2?.toFixed(1), unit: '%' },
                  ].map(({ label, val, unit }) => (
                    <Grid item xs={6} sm={4} md={2} key={label}>
                      <Box sx={{
                        textAlign: 'center', p: 2,
                        bgcolor: '#f8fafc', borderRadius: 2.5,
                        border: '1px solid #e2e8f0',
                        height: '100%', display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: BLUE, lineHeight: 1 }}>{val || '—'}</Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mt: 0.5 }}>{unit}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: '#94a3b8', fontSize: '0.65rem', mt: 0.25, textAlign: 'center' }}>{label}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {overview.abnormalBPCount > 0 && (
                  <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, p: 2, bgcolor: '#fff7ed', borderRadius: 2, border: '1px solid #fed7aa' }}>
                    <Warning sx={{ color: '#f59e0b', fontSize: 22, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 500 }}>
                      {overview.abnormalBPCount} patients with elevated BP (≥140/90 mmHg)
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Distribution Charts */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 2.5 }}>BMI Distribution</Typography>
                  {distribution?.bmiDistribution?.length ? (
                    distribution.bmiDistribution.map((b, i) => (
                      <BMIBar key={i} label={bmiLabels[i] || `Range ${i + 1}`} count={b.count} total={bmiTotal} color={bmiColors[i % bmiColors.length]} />
                    ))
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                      <Typography sx={{ color: '#94a3b8' }}>No BMI data available</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 2.5 }}>Blood Pressure Categories</Typography>
                  {distribution?.bpDistribution?.length ? (() => {
                    const bpTotal = distribution.bpDistribution.reduce((s, x) => s + x.count, 0);
                    const colors = { Normal: '#22c55e', Elevated: '#f59e0b', 'High Stage 1': '#ef4444', 'High Stage 2': '#7f1d1d' };
                    return distribution.bpDistribution.map((b, i) => (
                      <BMIBar key={i} label={b.category || b._id} count={b.count} total={bpTotal} color={colors[b.category || b._id] || BLUE} />
                    ));
                  })() : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 180 }}>
                      <Typography sx={{ color: '#94a3b8' }}>No BP data available</Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Per-Location Performance */}
          <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 2 }}>Performance by Location</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      {['Location', 'Patients', 'Sessions', 'Completed', 'Completion %'].map((h, i) => (
                        <TableCell key={h} align={i === 0 ? 'left' : 'center'}
                          sx={{ fontWeight: 700, color: DARK_BLUE, bgcolor: '#f0f9ff', py: 1.5, fontSize: '0.85rem' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {byLocation.map(loc => (
                      <TableRow key={loc.locationId} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ fontWeight: 600, color: '#1e293b', py: 1.75 }}>{loc.name}</TableCell>
                        <TableCell align="center" sx={{ color: '#475569', py: 1.75 }}>{loc.totalPatients}</TableCell>
                        <TableCell align="center" sx={{ color: '#475569', py: 1.75 }}>{loc.totalSessions}</TableCell>
                        <TableCell align="center" sx={{ color: '#475569', py: 1.75 }}>{loc.completedSessions}</TableCell>
                        <TableCell align="center" sx={{ py: 1.75 }}>
                          <Chip
                            label={`${loc.completionRate}%`}
                            size="small"
                            sx={{
                              fontWeight: 700, minWidth: 56,
                              bgcolor: loc.completionRate >= 80 ? '#dcfce7' : loc.completionRate >= 50 ? '#fff7ed' : '#fee2e2',
                              color: loc.completionRate >= 80 ? '#16a34a' : loc.completionRate >= 50 ? '#92400e' : '#dc2626',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!byLocation.length && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ color: '#94a3b8', py: 5 }}>No location data available</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}

      {/* ── MIS REPORT SECTION ─────────────────────────────────── */}
      <Divider sx={{ mb: 3, mt: 4 }}>
        <Chip label="MIS Report" sx={{ fontWeight: 600, color: DARK_BLUE, bgcolor: '#f0f9ff', border: `1px solid ${BLUE}33` }} />
      </Divider>

      <Card sx={{ borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 0.5 }}>
            Management Information System Report
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
            Generate a detailed operational report for any date range and export to Excel or PDF.
          </Typography>

          {/* Filters */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3, alignItems: 'flex-end' }}>
            <TextField
              label="From Date" type="date" size="small"
              value={misStart} onChange={e => setMisStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <TextField
              label="To Date" type="date" size="small"
              value={misEnd} onChange={e => setMisEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 170 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Location (optional)</InputLabel>
              <Select
                value={misLocation} label="Location (optional)"
                onChange={e => setMisLocation(e.target.value)}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              onClick={handleGenerateMis}
              disabled={misLoading}
              startIcon={misLoading ? <CircularProgress size={16} color="inherit" /> : <SessionIcon />}
              sx={{
                background: 'linear-gradient(135deg, #0891b2, #005B8F)',
                fontWeight: 700, height: 40,
                '&:hover': { background: 'linear-gradient(135deg, #005B8F, #0891b2)' },
              }}
            >
              {misLoading ? 'Generating…' : 'Generate MIS Report'}
            </Button>
          </Box>

          {misError && <Alert severity="error" sx={{ mb: 2 }}>{misError}</Alert>}

          {/* Summary KPIs */}
          {misData?.summary && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Patients', value: misData.summary.totalPatients ?? 0, color: BLUE },
                { label: 'Total Sessions', value: misData.summary.totalSessions ?? 0, color: '#8b5cf6' },
                { label: 'Completed Sessions', value: misData.summary.completedSessions ?? 0, color: '#10b981' },
                {
                  label: 'Completion Rate',
                  value: misData.summary.totalSessions > 0
                    ? `${Math.round((misData.summary.completedSessions / misData.summary.totalSessions) * 100)}%`
                    : '—',
                  color: '#f59e0b',
                },
              ].map(item => (
                <Grid item xs={6} sm={3} key={item.label}>
                  <Box sx={{
                    textAlign: 'center', p: 2, bgcolor: '#f8fafc',
                    borderRadius: 2.5, border: '1px solid #e2e8f0',
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: item.color, lineHeight: 1 }}>
                      {item.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, mt: 0.5, display: 'block' }}>
                      {item.label}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Vitals Summary */}
          {misData?.vitalSummary && (
            <Box sx={{ mb: 3, p: 2.5, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DARK_BLUE, mb: 1.5 }}>
                Vital Signs Summary
              </Typography>
              <Grid container spacing={2}>
                {[
                  { label: 'Avg BP', value: misData.vitalSummary.avgBP || '—' },
                  { label: 'Avg BMI', value: misData.vitalSummary.avgBMI != null ? Number(misData.vitalSummary.avgBMI).toFixed(1) : '—' },
                  { label: 'Avg SpO₂', value: misData.vitalSummary.avgSpO2 != null ? `${misData.vitalSummary.avgSpO2}%` : '—' },
                  { label: 'High BP Count', value: misData.vitalSummary.highBPCount ?? '—' },
                  { label: 'Low Hb Count', value: misData.vitalSummary.lowHbCount ?? '—' },
                  { label: 'Diabetic Alerts', value: misData.vitalSummary.diabeticCount ?? '—' },
                ].map(v => (
                  <Grid item xs={6} sm={4} md={2} key={v.label}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: BLUE }}>{v.value}</Typography>
                      <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block' }}>{v.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Results Table */}
          {misData && getMisTableRows().length > 0 && (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DARK_BLUE }}>
                  Breakdown
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleMisExportExcel}
                    sx={{ borderColor: '#10b981', color: '#10b981', fontWeight: 600, '&:hover': { borderColor: '#059669', bgcolor: '#ecfdf5' } }}
                  >
                    Export Excel
                  </Button>
                  <Button
                    variant="outlined" size="small"
                    startIcon={<DownloadIcon />}
                    onClick={handleMisExportPdf}
                    sx={{ borderColor: '#f59e0b', color: '#d97706', fontWeight: 600, '&:hover': { borderColor: '#b45309', bgcolor: '#fffbeb' } }}
                  >
                    Export PDF
                  </Button>
                </Box>
              </Box>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Date', 'Location', 'Patients', 'Completed', 'Completion Rate'].map((h, i) => (
                        <TableCell key={h} align={i < 2 ? 'left' : 'center'}
                          sx={{ fontWeight: 700, color: DARK_BLUE, bgcolor: '#f0f9ff', py: 1.5, fontSize: '0.85rem' }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getMisTableRows().map((row, idx) => (
                      <TableRow key={idx} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                        <TableCell sx={{ color: '#475569', py: 1.5 }}>{row.date}</TableCell>
                        <TableCell sx={{ color: '#1e293b', fontWeight: 500, py: 1.5 }}>{row.location}</TableCell>
                        <TableCell align="center" sx={{ color: '#475569', py: 1.5 }}>{row.patients}</TableCell>
                        <TableCell align="center" sx={{ color: '#475569', py: 1.5 }}>{row.completed}</TableCell>
                        <TableCell align="center" sx={{ py: 1.5 }}>
                          <Chip
                            label={`${row.rate}%`} size="small"
                            sx={{
                              fontWeight: 700, minWidth: 56,
                              bgcolor: row.rate >= 80 ? '#dcfce7' : row.rate >= 50 ? '#fff7ed' : '#fee2e2',
                              color: row.rate >= 80 ? '#16a34a' : row.rate >= 50 ? '#92400e' : '#dc2626',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1.5, textAlign: 'right' }}>
                Report generated at: {misData.generatedAt ? new Date(misData.generatedAt).toLocaleString() : new Date().toLocaleString()}
              </Typography>
            </>
          )}

          {misData && getMisTableRows().length === 0 && (
            <Box sx={{ textAlign: 'center', py: 5, color: '#94a3b8' }}>
              <SessionIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
              <Typography>No data found for the selected period and location.</Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: '#0f172a' }}>
          Export Patients to Excel
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            Data will be exported with masked Name, Email, and Mobile for privacy.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Start Date" type="date" size="small" fullWidth
              value={exportStart} onChange={(e) => setExportStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date" type="date" size="small" fullWidth
              value={exportEnd} onChange={(e) => setExportEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Location (optional)</InputLabel>
              <Select
                value={exportLocation} label="Location (optional)"
                onChange={(e) => setExportLocation(e.target.value)}
              >
                <MenuItem value="">All Locations</MenuItem>
                {locations.map(loc => (
                  <MenuItem key={loc._id} value={loc._id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {exportError && (
              <Typography variant="body2" color="error">{exportError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExportOpen(false)} disabled={exportLoading}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exportLoading}
            sx={{
              background: 'linear-gradient(135deg, #0891b2, #005B8F)',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #005B8F, #0891b2)' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Download Excel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
